import { Action, combineReducers } from "redux";
import { createReducer, IAction } from "reduxsauce";

import { Types as AllTypes } from "../../../actions";
import { IClass, ICourse } from "../../types/common";
import { FetchPartInstanceAnalytics, IUnitsAnalytics, IUser, IUsersAnalytics } from "../Common";

export interface IState {
  _: IClassAnalyticsState;
}

export interface IClassAnalyticsState {
  course: ICourse | null;
  class: IClass | null;
  fetchClassDataRequestState: FetchClassDataRequestState;
  fetchClassDataRequestError: string | null;
  fetchCourseDataRequestState: FetchCourseDataRequestState;
  fetchCourseDataRequestError: string | null;
  unitsAnalytics: IUnitsAnalytics[] | null;
  users: IUser[] | null;
  usersAnalytics: IUsersAnalytics | null;
  fetchAllUsersRequestState: FetchAllUsersRequestState;
  fetchAllUsersRequestError: string | null;
  fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState;
  fetchUsersAnalyticsRequestError: string | null;
  fetchClassPartInstSummaryRollup: FetchPartInstanceAnalytics;
}

export enum FetchAllUsersRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum FetchUsersAnalyticsRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum FetchClassDataRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum FetchCourseDataRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

const Types = (AllTypes as any)._APP._CLASS_ANALYTICS;

export const INITIAL_STATE: IClassAnalyticsState = {
  class: null,
  course: null,
  fetchAllUsersRequestError: null,
  fetchAllUsersRequestState: FetchAllUsersRequestState.IDLE,
  fetchClassDataRequestError: null,
  fetchClassDataRequestState: FetchClassDataRequestState.IDLE,
  fetchClassPartInstSummaryRollup: null,
  fetchCourseDataRequestError: null,
  fetchCourseDataRequestState: FetchCourseDataRequestState.IDLE,
  fetchUsersAnalyticsRequestError: null,
  fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.IDLE,
  unitsAnalytics: null,
  users: null,
  usersAnalytics: null,
};

export function mount(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return INITIAL_STATE;
}

export function unmount(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return INITIAL_STATE;
}

export function fetchClassData(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchClassDataRequestError: null,
    fetchClassDataRequestState: FetchClassDataRequestState.PENDING,
  };
}

export function handleFetchClassDataFailure(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchClassDataRequestError: action.error.message,
    fetchClassDataRequestState: FetchClassDataRequestState.FAILED,
  };
}

export function handleFetchClassDataSuccess(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    class: action.class,
    fetchClassDataRequestError: null,
    fetchClassDataRequestState: FetchClassDataRequestState.SUCCEEDED,
  };
}

export function fetchCourseData(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchCourseDataRequestError: null,
    fetchCourseDataRequestState: FetchCourseDataRequestState.PENDING,
  };
}

export function handleFetchCourseDataFailure(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchCourseDataRequestError: action.error.message,
    fetchCourseDataRequestState: FetchCourseDataRequestState.FAILED,
  };
}

export function handleFetchCourseDataSuccess(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    course: action.course,
    fetchCourseDataRequestError: null,
    fetchCourseDataRequestState: FetchCourseDataRequestState.SUCCEEDED,
  };
}

export function fetchAllUsers(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchAllUsersRequestError: null,
    fetchAllUsersRequestState: FetchAllUsersRequestState.PENDING,
  };
}

export function handleFetchAllUsersFailure(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchAllUsersRequestError: action.error.message,
    fetchAllUsersRequestState: FetchAllUsersRequestState.FAILED,
  };
}

export function handleFetchAllUsersSuccess(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchAllUsersRequestError: null,
    fetchAllUsersRequestState: FetchAllUsersRequestState.SUCCEEDED,
    users: action.users,
  };
}

export function fetchUsersAnalytics(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchUsersAnalyticsRequestError: null,
    fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.PENDING,
  };
}

export function handleFetchUsersAnalyticsFailure(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchUsersAnalyticsRequestError: action.error.message,
    fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.FAILED,
  };
}

export function handleFetchUsersAnalyticsSuccess(state: IClassAnalyticsState, action: IAction): IClassAnalyticsState {
  return {
    ...state,
    fetchClassPartInstSummaryRollup: action.fetchClassPartInstSummaryRollup,
    fetchUsersAnalyticsRequestError: null,
    fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.SUCCEEDED,
    unitsAnalytics: action.unitsAnalytics,
    usersAnalytics: action.usersAnalytics,
  };
}

export default combineReducers<IState, Action>({
  _: createReducer<IClassAnalyticsState>(INITIAL_STATE, {
    [Types.MOUNT]: mount,
    [Types.FETCH_CLASS_DATA]: fetchClassData,
    [Types.HANDLE_FETCH_CLASS_DATA_FAILURE]: handleFetchClassDataFailure,
    [Types.HANDLE_FETCH_CLASS_DATA_SUCCESS]: handleFetchClassDataSuccess,
    [Types.FETCH_COURSE_DATA]: fetchCourseData,
    [Types.HANDLE_FETCH_COURSE_DATA_FAILURE]: handleFetchCourseDataFailure,
    [Types.HANDLE_FETCH_COURSE_DATA_SUCCESS]: handleFetchCourseDataSuccess,
    [Types.FETCH_ALL_USERS]: fetchAllUsers,
    [Types.HANDLE_FETCH_ALL_USERS_FAILURE]: handleFetchAllUsersFailure,
    [Types.HANDLE_FETCH_ALL_USERS_SUCCESS]: handleFetchAllUsersSuccess,
    [Types.FETCH_USERS_ANALYTICS]: fetchUsersAnalytics,
    [Types.HANDLE_FETCH_USERS_ANALYTICS_FAILURE]: handleFetchUsersAnalyticsFailure,
    [Types.HANDLE_FETCH_USERS_ANALYTICS_SUCCESS]: handleFetchUsersAnalyticsSuccess,
    [Types.UNMOUNT]: unmount,
  }),
});
